import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Megaphone, XCircle, MessageSquareWarning, ExternalLink, Check, FileSignature, UserCog, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { goToReference } from '@/lib/openReference';
import { formatCurrency } from '@/lib/formatters';
import { contractPath } from '@/lib/openReference';
import { usePermissions } from '@/context/PermissionsContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { dayLabelOf, timeLabel } from '@/components/chat/chatShared';
import { chatSocket } from '@/components/chat/chatSocket';

const TYPE = { incomes: 'ingreso', expenses: 'egreso', funds: 'pedido de dinero' };
const OUTCOME = {
  reopened: ['fue reabierta: el movimiento ya está en Pendientes para corregirse', 'sky'],
  done: ['fue atendida', 'emerald'],
  rejected: ['fue rechazada', 'rose'],
  void: ['quedó sin efecto', 'slate'],
};
const ERROR_OUTCOME = { done: ['fue resuelto', 'emerald'], rejected: ['fue descartado', 'rose'], void: ['quedó sin efecto', 'slate'] };
// «Tu solicitud de edición / eliminación» o «Tu reporte de error»
const reqTitle = (r) => (r.request_type === 'error' ? 'Tu reporte de error' : `Tu solicitud de ${r.request_type === 'delete' ? 'eliminación' : 'edición'}`);
const TONE = {
  orange: { icon: 'bg-[#FF6B00] text-white', bg: 'bg-orange-50/60' },
  rose: { icon: 'bg-rose-600 text-white', bg: 'bg-rose-50/70' },
  emerald: { icon: 'bg-emerald-600 text-white', bg: 'bg-emerald-50/70' },
  sky: { icon: 'bg-sky-600 text-white', bg: 'bg-sky-50/70' },
  slate: { icon: 'bg-slate-500 text-white', bg: 'bg-slate-50' },
  read: { icon: 'bg-slate-100 text-slate-500', bg: '' },
};

const contractLabel = (c) => `Contrato ${c.folio}${c.tenant_name ? ` · ${c.tenant_name}` : ''}${c.unit_number ? ` · ${c.unit_number}` : ''}`;

// Campana: avisos del equipo + rechazos en Verificar + resolución de mis solicitudes (todo en un solo lugar)
export const NoticeBell = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canRejections = can('verificar', 'rechazados') || can('verificar', 'ver');
  const canContracts = can('con_contratos', 'ver');
  const [notices, setNotices] = useState([]);
  const [rejections, setRejections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [open, setOpen] = useState(false);
  const toasted = useRef(new Set());
  const activeToast = useRef(null); // un solo emergente a la vez: los demás esperan en la campana
  const showOne = useCallback((id, fn, title, opts) => {
    if (activeToast.current && activeToast.current !== id) return false;
    activeToast.current = id; toasted.current.add(id);
    fn(title, { ...opts, id, duration: Infinity, position: 'bottom-right', onDismiss: () => { if (activeToast.current === id) activeToast.current = null; } });
    return true;
  }, []);
  const closeToast = useCallback((id) => { toast.dismiss(id); if (activeToast.current === id) activeToast.current = null; }, []);

  const acceptContract = useCallback((contractId) => {
    setContracts((cs) => cs.filter((c) => c.contract_id !== contractId));
    closeToast(`ctr-${contractId}`);
    api.post(`/contracts/${contractId}/review-read`, {}, { silent: true }).catch(() => {});
  }, [closeToast]);
  useEffect(() => {
    for (const c of contracts.filter((c) => !toasted.current.has(`ctr-${c.contract_id}`))) {
      const approved = c.status !== 'rejected';
      const ok = showOne(`ctr-${c.contract_id}`, approved ? toast.success : toast.error, `${contractLabel(c)} fue ${approved ? 'aprobado: ya está vigente' : 'rechazado'}`, {
        description: approved ? `Ocupa la unidad e inicia la cobranza${c.reviewed_by_name ? ` · ${c.reviewed_by_name}` : ''}` : `Motivo: ${c.review_note || '—'}${c.reviewed_by_name ? ` · ${c.reviewed_by_name}` : ''}. Corrígelo en Contratos y reenvíalo.`,
        action: { label: 'Aceptar', onClick: () => acceptContract(c.contract_id) },
      });
      if (!ok) break;
    }
  }, [contracts, acceptContract, showOne]);

  const acceptAssignment = useCallback((tenantId) => {
    setAssignments((as) => as.filter((a) => a.tenant_id !== tenantId));
    closeToast(`asg-${tenantId}`);
    api.post(`/tenants/${tenantId}/assignment-read`, {}, { silent: true }).catch(() => {});
  }, [closeToast]);
  // Me asignaron verificar un expediente: emergente + campana
  useEffect(() => {
    for (const a of assignments.filter((a) => !toasted.current.has(`asg-${a.tenant_id}`))) {
      const ok = showOne(`asg-${a.tenant_id}`, toast.info, `Te asignaron verificar el expediente de «${a.full_name}»`, {
        description: `${a.assigned_by_name ? `${a.assigned_by_name} · ` : ''}${a.complete ? 'Expediente completo: cotéjalo y márcalo verificado.' : 'Aún le faltan datos; podrás verificarlo cuando esté completo.'}`,
        action: { label: 'Ver expediente', onClick: () => { acceptAssignment(a.tenant_id); navigate(`/app/tenants/${a.tenant_id}?verify=1`); } },
      });
      if (!ok) break;
    }
  }, [assignments, acceptAssignment, showOne, navigate]);

  const acceptOutcome = useCallback((tenantId) => {
    setOutcomes((os) => os.filter((o) => o.tenant_id !== tenantId));
    closeToast(`out-${tenantId}`);
    api.post(`/tenants/${tenantId}/outcome-read`, {}, { silent: true }).catch(() => {});
  }, [closeToast]);
  // El responsable que asigné ya verificó o rechazó: emergente + campana para quien asignó
  useEffect(() => {
    for (const o of outcomes.filter((o) => !toasted.current.has(`out-${o.tenant_id}`))) {
      const ok = showOne(`out-${o.tenant_id}`, o.outcome === 'verified' ? toast.success : toast.error, `${o.by_name || 'El responsable'} ${o.outcome === 'verified' ? 'verificó' : 'rechazó'} el expediente de «${o.full_name}»`, {
        description: o.outcome === 'verified' ? 'El expediente quedó sellado como verificado.' : `Motivo: ${(o.reason || '').replace(/^Rechazado:\s*/, '') || '—'}`,
        action: { label: 'Aceptar', onClick: () => acceptOutcome(o.tenant_id) },
      });
      if (!ok) break;
    }
  }, [outcomes, acceptOutcome, showOne]);

  const acceptRequest = useCallback((requestId) => {
    setRequests((rs) => rs.filter((r) => r.request_id !== requestId));
    closeToast(`req-${requestId}`);
    api.post(`/review/edit-requests/${requestId}/read`, {}, { silent: true }).catch(() => {});
  }, [closeToast]);

  // Solicitud atendida: aviso emergente que el solicitante debe aceptar (además de quedar en la campana)
  useEffect(() => {
    for (const r of requests.filter((r) => r.status === 'done' && !toasted.current.has(`req-${r.request_id}`))) {
      const label = r.label || r.movement?.concept || r.movement?.category || 'registro';
      const ok = showOne(`req-${r.request_id}`, toast.success, `${reqTitle(r)} sobre «${label}» ${r.request_type === 'error' ? 'fue resuelto' : 'fue atendida'}`, {
        description: `${r.resolution_note || (r.request_type === 'error' ? 'El problema ya fue revisado' : 'El cambio ya fue aplicado')}${r.resolved_by_name ? ` · ${r.resolved_by_name}` : ''}`,
        action: { label: 'Aceptar', onClick: () => acceptRequest(r.request_id) },
      });
      if (!ok) break;
    }
  }, [requests, acceptRequest, showOne]);

  const load = useCallback(() => {
    api.get('/notices', { silent: true, noCache: true }).then((r) => setNotices(r.data.notices || [])).catch(() => {});
    if (canRejections) api.get('/review/my-rejections', { silent: true, noCache: true }).then(({ data }) => setRejections(data.items || [])).catch(() => {});
    api.get('/review/edit-requests/my-resolved', { silent: true, noCache: true }).then(({ data }) => setRequests(data.items || [])).catch(() => {});
    if (canContracts) api.get('/contracts/review/my-notices', { silent: true, noCache: true }).then(({ data }) => setContracts(data.items || [])).catch(() => {});
    api.get('/tenants/review/my-assignments', { silent: true, noCache: true }).then(({ data }) => setAssignments(data.items || [])).catch(() => {});
    api.get('/tenants/review/my-assignment-outcomes', { silent: true, noCache: true }).then(({ data }) => setOutcomes(data.items || [])).catch(() => {});
  }, [canRejections, canContracts]);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load, pathname]);
  // Tiempo real: el servidor emite notice.new (audience team) por el WebSocket del chat cuando nace algo para la campana
  useEffect(() => chatSocket.on((ev) => { if (ev.type === 'notice.new' && ev.audience === 'team') load(); }), [load]);

  const items = useMemo(() => {
    const list = [
      ...rejections.map((r) => ({
        id: `rej-${r.rejection_id}`, tone: 'rose', icon: XCircle, ts: r.rejected_at || r.created_at, unread: true, testId: `rejection-notice-${r.rejection_id}`,
        title: `Tu ${TYPE[r.tab] || 'registro'} «${r.concept}» por ${formatCurrency(r.amount)} fue rechazado${r.rejected_by_name ? ` por ${r.rejected_by_name}` : ''}.`,
        body: `Motivo: ${r.reason || '—'}`, dismiss: () => api.post(`/review/rejections/${r.rejection_id}/read`, {}, { silent: true }),
      })),
      ...requests.map((r) => {
        const [text, tone] = (r.request_type === 'error' ? ERROR_OUTCOME[r.status] : OUTCOME[r.status]) || OUTCOME.void;
        const label = r.label || r.movement?.concept || r.movement?.category || 'registro';
        return {
          id: `req-${r.request_id}`, tone, icon: MessageSquareWarning, ts: r.resolved_at || r.created_at, unread: true, testId: `edit-request-notice-${r.request_id}`,
          title: `${reqTitle(r)} sobre «${label}» ${text}${r.resolved_by_name && r.status !== 'void' ? ` (${r.resolved_by_name})` : ''}.`,
          body: `${r.request_type === 'error' ? 'Problema' : 'Motivo'}: ${r.reason}${r.resolution_note ? ` · Respuesta: ${r.resolution_note}` : ''}`,
          accept: true,
          dismiss: () => { closeToast(`req-${r.request_id}`); return api.post(`/review/edit-requests/${r.request_id}/read`, {}, { silent: true }); },
          openRef: r.status !== 'void' && r.kind !== 'system' ? async () => { try { const { data } = await api.get(`/review/edit-requests/${r.request_id}/reference`, { noCache: true }); setOpen(false); goToReference(navigate, data); } catch { /* noop */ } } : null,
        };
      }),
      ...contracts.map((c) => {
        const approved = c.status !== 'rejected';
        return {
          id: `ctr-${c.contract_id}`, tone: approved ? 'emerald' : 'rose', icon: FileSignature, ts: c.reviewed_at, unread: true, accept: true, testId: `contract-review-notice-${c.contract_id}`,
          title: `${contractLabel(c)} fue ${approved ? 'aprobado y ya está vigente' : 'rechazado'}${c.reviewed_by_name ? ` (${c.reviewed_by_name})` : ''}.`,
          body: approved ? 'La unidad queda ocupada, inicia la cobranza y el inquilino puede entrar al portal.' : `Motivo: ${c.review_note || '—'} · Corrígelo en Contratos y usa «Reenviar a verificación».`,
          dismiss: () => { closeToast(`ctr-${c.contract_id}`); return api.post(`/contracts/${c.contract_id}/review-read`, {}, { silent: true }); },
          openRef: () => { setOpen(false); navigate(contractPath(c.contract_id)); },
        };
      }),
      ...assignments.map((a) => ({
        id: `asg-${a.tenant_id}`, tone: 'sky', icon: UserCog, ts: a.assigned_at, unread: true, accept: true, testId: `assignment-notice-${a.tenant_id}`,
        title: `Te asignaron verificar el expediente de «${a.full_name}»${a.assigned_by_name ? ` (${a.assigned_by_name})` : ''}.`,
        body: a.complete ? 'Expediente completo: cotéjalo y márcalo verificado desde su perfil.' : 'Aún le faltan datos; podrás verificarlo cuando esté completo.',
        dismiss: () => { closeToast(`asg-${a.tenant_id}`); return api.post(`/tenants/${a.tenant_id}/assignment-read`, {}, { silent: true }); },
        openRef: () => { setOpen(false); navigate(`/app/tenants/${a.tenant_id}?verify=1`); },
      })),
      ...outcomes.map((o) => ({
        id: `out-${o.tenant_id}`, tone: o.outcome === 'verified' ? 'emerald' : 'rose', icon: o.outcome === 'verified' ? ShieldCheck : XCircle, ts: o.outcome_at, unread: true, accept: true, testId: `assignment-outcome-notice-${o.tenant_id}`,
        title: `${o.by_name || 'El responsable'} ${o.outcome === 'verified' ? 'verificó' : 'rechazó'} el expediente de «${o.full_name}».`,
        body: o.outcome === 'verified' ? 'Quedó sellado como verificado.' : `Motivo: ${(o.reason || '').replace(/^Rechazado:\s*/, '') || '—'}`,
        dismiss: () => { closeToast(`out-${o.tenant_id}`); return api.post(`/tenants/${o.tenant_id}/outcome-read`, {}, { silent: true }); },
        openRef: () => { setOpen(false); navigate(`/app/tenants/${o.tenant_id}`); },
      })),
      ...notices.map((n) => ({
        id: `not-${n.notice_id}`, tone: 'orange', icon: Megaphone, ts: n.created_at, unread: !n.read_at, testId: `notice-bell-item-${n.notice_id}`,
        title: n.title, body: n.body, meta: n.created_by_name, keep: true,
        openRef: n.title === 'Tarea asignada' ? () => { setOpen(false); navigate('/app/tasks'); } : undefined,
        dismiss: () => api.post(`/notices/${n.notice_id}/read`),
      })),
    ];
    return list.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
  }, [rejections, requests, contracts, assignments, outcomes, notices, navigate, closeToast]);
  const unread = items.filter((i) => i.unread).length;

  const markOne = async (it) => {
    if (!it.unread) return;
    if (it.keep) setNotices((ns) => ns.map((n) => (`not-${n.notice_id}` === it.id ? { ...n, read_at: new Date().toISOString() } : n)));
    else { setRejections((rs) => rs.filter((r) => `rej-${r.rejection_id}` !== it.id)); setRequests((rs) => rs.filter((r) => `req-${r.request_id}` !== it.id)); setContracts((cs) => cs.filter((c) => `ctr-${c.contract_id}` !== it.id)); }
    try { await it.dismiss(); } catch { load(); }
  };
  const markAll = async () => {
    setNotices((ns) => ns.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))); setRejections([]); setRequests([]); setContracts([]);
    requests.forEach((r) => closeToast(`req-${r.request_id}`)); contracts.forEach((c) => closeToast(`ctr-${c.contract_id}`));
    try {
      await Promise.all([api.post('/notices/read-all'), canRejections ? api.post('/review/rejections/read-all', {}, { silent: true }) : null, ...requests.map((r) => api.post(`/review/edit-requests/${r.request_id}/read`, {}, { silent: true })), ...contracts.map((c) => api.post(`/contracts/${c.contract_id}/review-read`, {}, { silent: true }))]);
    } catch { load(); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button type="button" className="relative rounded-full p-2 transition hover:bg-white/10" title="Avisos" aria-label="Avisos" data-testid="topbar-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#FF6B00] px-1 font-mono text-[10px] font-bold text-white ring-2 ring-inmoblum-blue" data-testid="topbar-bell-count">{unread > 99 ? '99+' : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] max-w-[calc(100vw-1rem)] p-0" data-testid="notice-bell-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-bold text-slate-900">Avisos{unread > 0 && <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 font-mono text-[11px] text-[#FF6B00]">{unread} sin leer</span>}</p>
          {unread > 0 && <button type="button" onClick={markAll} className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900" data-testid="notice-bell-read-all"><CheckCheck className="h-3.5 w-3.5" />Marcar todo leído</button>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {!items.length && <p className="px-4 py-10 text-center text-sm text-slate-500" data-testid="notice-bell-empty">No tienes avisos por ahora.</p>}
          {items.map((it) => {
            const t = it.unread ? TONE[it.tone] : TONE.read; const Icon = it.icon;
            return (
              <div key={it.id} className={`flex gap-3 border-b border-slate-100 px-4 py-3 last:border-0 ${t.bg}`} data-testid={it.testId}>
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${t.icon}`}><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${it.unread ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>{it.title}</p>
                  <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-600">{it.body}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                    <span>{it.meta ? `${it.meta} · ` : ''}{it.ts ? `${dayLabelOf(it.ts)} ${timeLabel(it.ts)}` : ''}</span>
                    {it.openRef && <button type="button" onClick={it.openRef} className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900" data-testid={`${it.testId}-ref`}><ExternalLink className="h-3 w-3" />{it.title === 'Tarea asignada' ? 'Ver tareas' : 'Ver registro'}</button>}
                    {it.unread && <button type="button" onClick={() => markOne(it)} className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900" data-testid={`${it.testId}-dismiss`}><Check className="h-3 w-3" />{it.accept ? 'Aceptar' : it.keep ? 'Enterado' : 'Entendido'}</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
